import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BriefcaseBusiness, Inbox, Loader2, LogIn, Megaphone, Plus, RefreshCw } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import BrandRule from '../../components/brand/BrandRule';
import InlineAlert from '../../components/common/InlineAlert';
import LockedCard from '../../components/gating/LockedCard';
import { ACCOUNT_SUSPENDED_MESSAGE_KEY, apiErrorInfo } from '../../utils/apiErrorInfo';
import type { Advertisement } from '../../services/advertisementService';
import { boardIssueOf, listOpenRequests, type BoardIssue } from './boardApi';
import AdItem from './AdItem';
import NewRequestForm from './NewRequestForm';

/**
 * «لوحة الطلبات», the Requests board: custom real-estate requests that
 * professional accounts post and answer.
 *
 * Professional accounts only (owner, 2026-10). The route sits inside
 * ProfessionalRoute (router/config.tsx), and the API enforces the same rule on
 * every call, reads included (requireProfessional). If the server still says
 * no (403 PROFESSIONAL_ACCOUNT_REQUIRED), or the sign-in is gone (401), the
 * board gives way to the matching locked card.
 */
export default function AdvertisementsPage() {
  const { t, i18n } = useTranslation(['ads', 'gating', 'common']);
  const { session, user } = useAuth();
  const location = useLocation();
  const token = session?.token ?? null;
  const userId = user?.id ?? null;
  const listHeadingId = useId();

  const [ads, setAds] = useState<Advertisement[]>([]);
  const [loading, setLoading] = useState(true);
  // i18n keys, so a language switch re-renders the messages too.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [issue, setIssue] = useState<BoardIssue | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const postButtonRef = useRef<HTMLButtonElement>(null);
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const restorePostFocus = useRef(false);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!token) return;
    const seq = ++requestSeq.current;
    setLoading(true);
    setLoadError(null);
    setIssue(null);
    try {
      const list = await listOpenRequests(token);
      if (seq !== requestSeq.current) return;
      setAds(list);
    } catch (error) {
      if (seq !== requestSeq.current) return;
      const access = boardIssueOf(error);
      const { status, code } = apiErrorInfo(error);
      if (access) setIssue(access);
      else if (status === 404) setIssue('disabled');
      else if (code === 'ACCOUNT_SUSPENDED') setLoadError(ACCOUNT_SUSPENDED_MESSAGE_KEY);
      else setLoadError(code === 'PROFESSIONAL_CHECK_FAILED' ? 'gating:pro.checkFailed' : 'errors.load');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!formOpen && restorePostFocus.current) {
      restorePostFocus.current = false;
      postButtonRef.current?.focus();
    }
  }, [formOpen]);

  const handleAccessIssue = useCallback((next: BoardIssue) => setIssue(next), []);

  const openForm = () => {
    setNotice(null);
    setFormOpen(true);
  };

  const closeForm = () => {
    restorePostFocus.current = true;
    setFormOpen(false);
  };

  const handlePosted = (created: Advertisement) => {
    setAds((list) => [
      { ...created, _count: created._count ?? { replies: 0 } },
      ...list.filter((ad) => ad.id !== created.id),
    ]);
    setNotice('form.published');
    closeForm();
  };

  const handleReplied = useCallback((id: string) => {
    setAds((list) =>
      list.map((ad) => (ad.id === id ? { ...ad, _count: { replies: (ad._count?.replies ?? 0) + 1 } } : ad))
    );
    setNotice('detail.replySent');
  }, []);

  const handleClosed = useCallback((id: string) => {
    setAds((list) => list.filter((ad) => ad.id !== id));
    setOpenId((current) => (current === id ? null : current));
    setNotice('detail.closed');
    // The card that held focus is gone; land on the list it left.
    listHeadingRef.current?.focus();
  }, []);

  // With professional roles on, an existing account adds a role at
  // /account/professional; without them, the way in is a broker signup.
  // Same choice as components/auth/ProfessionalRoute.
  const proRolesEnabled = import.meta.env.VITE_PRO_ROLES_ENABLED === 'true';
  const pageIssue: BoardIssue | null = token ? issue : 'session';

  let content: ReactNode;
  if (pageIssue === 'professional') {
    content = (
      <LockedCard
        icon={BriefcaseBusiness}
        title={t('gating:pro.title')}
        body={t('gating:pro.body')}
        primary={
          proRolesEnabled
            ? { label: t('gating:pro.upgrade'), to: '/account/professional' }
            : { label: t('gating:pro.register'), to: '/register?role=BROKER' }
        }
        secondary={{ label: t('gating:pro.backHome'), to: '/' }}
        className="my-4 sm:my-8"
      />
    );
  } else if (pageIssue === 'session') {
    content = (
      <LockedCard
        icon={LogIn}
        title={t('session.title')}
        body={t('session.body')}
        primary={{ label: t('session.signIn'), to: '/signin', state: { from: location } }}
        className="my-4 sm:my-8"
      />
    );
  } else {
    const disabled = pageIssue === 'disabled';
    content = (
      <>
        <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex min-w-0 items-start gap-3">
            <span
              aria-hidden="true"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-border-soft)]"
            >
              <Megaphone className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <h1 className="text-2xl font-bold leading-tight text-[var(--pv-color-primary)]">{t('title')}</h1>
              <BrandRule className="mt-2.5" />
              <p className="mt-3 text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">{t('subtitle')}</p>
            </div>
          </div>
          {!disabled && !formOpen ? (
            <Button ref={postButtonRef} onClick={openForm} className="w-full shrink-0 sm:w-auto">
              <Plus className="h-5 w-5 shrink-0" aria-hidden="true" />
              {t('post')}
            </Button>
          ) : null}
        </header>

        {/* Always mounted, so screen readers announce each confirmation. */}
        <div role="status" aria-live="polite">
          {notice ? (
            <InlineAlert tone="success" className="mt-6">
              {t(notice)}
            </InlineAlert>
          ) : null}
        </div>

        {disabled ? (
          <p className="mt-8 rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-soft)] px-5 py-10 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {t('errors.disabled')}
          </p>
        ) : (
          <>
            {formOpen && token ? (
              <NewRequestForm
                token={token}
                onPosted={handlePosted}
                onCancel={closeForm}
                onAccessIssue={handleAccessIssue}
              />
            ) : null}

            <section aria-labelledby={listHeadingId} className="mt-8">
              <h2
                id={listHeadingId}
                ref={listHeadingRef}
                tabIndex={-1}
                className="text-lg font-bold text-[var(--pv-color-primary)] focus:outline-none"
              >
                {t('list.heading')}
              </h2>
              <div className="mt-4">
                {loading && ads.length === 0 ? (
                  <p
                    role="status"
                    className="flex items-center justify-center gap-2 py-12 text-[0.9375rem] text-[var(--pv-color-text-muted)]"
                  >
                    <Loader2
                      className="h-[1.125rem] w-[1.125rem] shrink-0 animate-spin text-[var(--pv-color-primary)] motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                    {t('list.loading')}
                  </p>
                ) : loadError ? (
                  <InlineAlert
                    live="alert"
                    action={
                      loadError === ACCOUNT_SUSPENDED_MESSAGE_KEY ? undefined : (
                        <Button variant="secondary" size="sm" onClick={() => void load()}>
                          <RefreshCw className="h-4 w-4" aria-hidden="true" />
                          {t('errors.retry')}
                        </Button>
                      )
                    }
                  >
                    {t(loadError)}
                  </InlineAlert>
                ) : ads.length === 0 ? (
                  <div className="rounded-2xl border border-[var(--pv-border-soft)] bg-[var(--pv-color-surface-strong)] px-6 py-12 text-center">
                    <span
                      aria-hidden="true"
                      className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)] ring-1 ring-inset ring-[var(--pv-border-soft)]"
                    >
                      <Inbox className="h-6 w-6" />
                    </span>
                    <p className="mt-4 text-base font-bold text-[var(--pv-color-text)]">{t('list.empty')}</p>
                    <p className="mt-1 text-[0.9375rem] text-[var(--pv-color-text-muted)]">{t('list.emptyHint')}</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {ads.map((ad) => (
                      <AdItem
                        key={ad.id}
                        ad={ad}
                        token={token}
                        currentUserId={userId}
                        open={openId === ad.id}
                        onToggle={() => setOpenId((current) => (current === ad.id ? null : ad.id))}
                        onAccessIssue={handleAccessIssue}
                        onReplied={handleReplied}
                        onClosed={handleClosed}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </section>
          </>
        )}
      </>
    );
  }

  return (
    <div dir={i18n.dir()} className="flex min-h-screen flex-col bg-[var(--pv-color-surface)]">
      <Navigation />
      <main id="main-content" className="flex-1">
        <div className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-6 sm:py-10">{content}</div>
      </main>
      <Footer />
    </div>
  );
}
